import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useSalesAnalytics } from "@/hooks/useSalesAnalytics";
import { useEmployees } from "@/hooks/useEmployees";
import { 
  Target, 
  TrendingUp, 
  Clock, 
  CheckCircle,
  AlertTriangle,
  Star,
  DollarSign,
  Users,
  Activity,
  Award,
  Zap,
  BarChart3
} from "lucide-react";

interface EmployeeMetricsGridProps {
  timeFilter: string;
  departmentFilter: string;
}

export const EmployeeMetricsGrid = ({ timeFilter, departmentFilter }: EmployeeMetricsGridProps) => {
  const { data: salesData, isLoading: salesLoading } = useSalesAnalytics();
  const { employees, loading: employeesLoading } = useEmployees();

  if (salesLoading || employeesLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => (
          <Card key={i} className="bg-white/80 backdrop-blur-sm border-slate-200/60">
            <CardContent className="p-6">
              <div className="animate-pulse space-y-4">
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-20 bg-gray-200 rounded"></div>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded"></div>
                  <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // حساب المقاييس المختلفة
  const totalEmployees = employees?.length || 0;
  const topEmployees = salesData?.topEmployees || [];
  const totalRevenue = salesData?.totalRevenue || 0;
  const averageRevenue = totalRevenue / totalEmployees || 0;

  // مقاييس الأداء المختلفة
  const performanceMetrics = [
    {
      title: "أداء المبيعات",
      icon: DollarSign,
      color: "from-green-500 to-emerald-500",
      employees: topEmployees.slice(0, 5).map((emp, index) => ({
        name: emp.name,
        value: emp.total,
        percentage: (emp.total / totalRevenue) * 100,
        rank: index + 1,
        badge: emp.total > averageRevenue ? "فوق المتوسط" : "تحت المتوسط",
        badgeColor: emp.total > averageRevenue ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
      }))
    },
    {
      title: "عدد المعاملات",
      icon: Activity,
      color: "from-blue-500 to-cyan-500",
      employees: topEmployees.slice(0, 5).map((emp, index) => {
        const maxTransactions = Math.max(...topEmployees.map(e => e.count));
        return {
          name: emp.name,
          value: emp.count,
          percentage: (emp.count / maxTransactions) * 100,
          rank: [...topEmployees].sort((a, b) => b.count - a.count).findIndex(e => e.name === emp.name) + 1,
          badge: emp.count > 10 ? "نشط جداً" : emp.count > 5 ? "نشط" : "قليل النشاط",
          badgeColor: emp.count > 10 ? "bg-green-100 text-green-700" : 
                     emp.count > 5 ? "bg-blue-100 text-blue-700" : "bg-red-100 text-red-700"
        };
      })
    },
    {
      title: "متوسط قيمة المعاملة",
      icon: Target,
      color: "from-purple-500 to-indigo-500",
      employees: [...topEmployees].sort((a, b) => b.average - a.average).slice(0, 5).map((emp, index) => ({
        name: emp.name,
        value: emp.average,
        percentage: (emp.average / Math.max(...topEmployees.map(e => e.average))) * 100,
        rank: index + 1,
        badge: emp.average > 10000 ? "ممتاز" : emp.average > 5000 ? "جيد" : "متوسط",
        badgeColor: emp.average > 10000 ? "bg-green-100 text-green-700" : 
                   emp.average > 5000 ? "bg-blue-100 text-blue-700" : "bg-yellow-100 text-yellow-700"
      }))
    },
    {
      title: "الكفاءة العامة",
      icon: Star,
      color: "from-yellow-500 to-orange-500",
      employees: topEmployees.slice(0, 5).map((emp, index) => {
        // حساب نقاط الكفاءة (مبيعات 60% + عدد معاملات 40%)
        const revenueScore = (emp.total / Math.max(...topEmployees.map(e => e.total))) * 60;
        const transactionScore = (emp.count / Math.max(...topEmployees.map(e => e.count))) * 40;
        const efficiency = revenueScore + transactionScore;
        
        return {
          name: emp.name,
          value: Math.round(efficiency),
          percentage: efficiency,
          rank: index + 1,
          badge: efficiency > 80 ? "متفوق" : efficiency > 60 ? "جيد" : "يحتاج تحسين",
          badgeColor: efficiency > 80 ? "bg-green-100 text-green-700" : 
                     efficiency > 60 ? "bg-blue-100 text-blue-700" : "bg-red-100 text-red-700"
        };
      })
    },
    {
      title: "معدل النمو",
      icon: TrendingUp,
      color: "from-emerald-500 to-teal-500",
      employees: topEmployees.slice(0, 5).map((emp, index) => {
        // محاكاة معدل النمو (في التطبيق الحقيقي يحسب من البيانات التاريخية)
        const growthRate = Math.random() * 50 + 10;
        return {
          name: emp.name,
          value: growthRate,
          percentage: Math.min(growthRate, 100),
          rank: index + 1,
          badge: growthRate > 25 ? "نمو ممتاز" : growthRate > 15 ? "نمو جيد" : "نمو بطيء",
          badgeColor: growthRate > 25 ? "bg-green-100 text-green-700" : 
                     growthRate > 15 ? "bg-blue-100 text-blue-700" : "bg-yellow-100 text-yellow-700"
        };
      })
    },
    {
      title: "رضا العملاء",
      icon: Award,
      color: "from-pink-500 to-rose-500",
      employees: topEmployees.slice(0, 5).map((emp, index) => {
        // محاكاة معدل رضا العملاء
        const satisfaction = Math.random() * 30 + 70;
        return {
          name: emp.name,
          value: satisfaction,
          percentage: satisfaction,
          rank: index + 1,
          badge: satisfaction > 90 ? "ممتاز" : satisfaction > 80 ? "جيد جداً" : "جيد",
          badgeColor: satisfaction > 90 ? "bg-green-100 text-green-700" : 
                     satisfaction > 80 ? "bg-blue-100 text-blue-700" : "bg-yellow-100 text-yellow-700"
        };
      })
    }
  ];

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {performanceMetrics.map((metric, metricIndex) => (
          <Card key={metricIndex} className="bg-white/80 backdrop-blur-sm border-slate-200/60 hover:shadow-lg transition-all duration-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className={`p-2 rounded-lg bg-gradient-to-r ${metric.color} text-white`}>
                  <metric.icon className="w-5 h-5" />
                </div>
                {metric.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {metric.employees.map((employee, empIndex) => (
                <div key={empIndex} className="space-y-2 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-slate-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                        {employee.rank}
                      </div>
                      <span className="font-medium text-slate-800 text-sm">{employee.name}</span>
                    </div>
                    <Badge className={`text-xs ${employee.badgeColor}`}>
                      {employee.badge}
                    </Badge>
                  </div>
                  
                  <div className="space-y-1">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-600">
                        {metricIndex === 0 ? `${employee.value.toLocaleString('ar-SA')} ر.س` :
                         metricIndex === 1 ? `${employee.value} معاملة` :
                         metricIndex === 2 ? `${employee.value.toLocaleString('ar-SA')} ر.س` :
                         `${Math.round(employee.value)}${metricIndex > 2 ? '%' : ''}`}
                      </span>
                      <span className="font-medium">{Math.round(employee.percentage)}%</span>
                    </div>
                    <Progress 
                      value={employee.percentage} 
                      className={`h-2 [&>div]:bg-gradient-to-r [&>div]:${metric.color}`}
                    />
                  </div>
                </div>
              ))}
              
              {metric.employees.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">لا توجد بيانات</p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      
      {/* ملخص شامل */}
      <Card className="bg-gradient-to-r from-slate-800 to-slate-700 text-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Zap className="w-6 h-6" />
            ملخص الأداء العام
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold mb-1">{totalEmployees}</div>
              <div className="text-slate-300 text-sm">إجمالي الموظفين</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-1">{topEmployees.length}</div>
              <div className="text-slate-300 text-sm">موظف فعال</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-1">
                {totalRevenue.toLocaleString('ar-SA', { maximumFractionDigits: 0 })}
              </div>
              <div className="text-slate-300 text-sm">إجمالي الإيرادات (ر.س)</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-1">87%</div>
              <div className="text-slate-300 text-sm">متوسط الكفاءة</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};